import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useFinishes } from '@/api/finishes';
import { DEFAULT_PANEL_FINISH, componentDims, resolveFoamLayers, resolveOversize } from '@estimator/shared';
import PanelDimControls from './shared/PanelDimControls';
import ResizableModal from '@/components/shared/ResizableModal';
import {
  ModalHeader, ModalBody, ModalFooter,
  Button, Input, Divider, Select, SelectItem, Switch, Accordion, AccordionItem,
  useDisclosure,
} from '@heroui/react';
import { Sofa, Layers } from 'lucide-react';
import { toast } from 'sonner';
import { useCreateLineItem, useUpdateLineItem } from '@/api/line-items';
import { useMaterialsLibrary } from '@/api/materials-library';
import { useBanquetteBaseTemplates } from '@/api/banquette-base-templates';
import { useHardwareLibrary } from '@/api/hardware-library';
import { useUpholsteryDesignSettings } from '@/api/upholstery-design-settings';
import { useUpholsteryDesigns } from '@/api/upholstery-designs';
import { useOversizeRulesState } from '@/api/oversize-settings';
import UpholsteryDesignSelector from './upholstery/UpholsteryDesignSelector';

import BanquetteFramingConfig from './banquette/BanquetteFramingConfig';
import BanquetteViewportSelector from './banquette/BanquetteViewportSelector';
import BanquetteCostPreview from './banquette/BanquetteCostPreview';
import BanquetteSlotManager from './banquette/BanquetteSlotManager';
import BanquetteFinishSummary from './banquette/BanquetteFinishSummary';
import FinishSchedule from './FinishSchedule';
import ComponentNestingPanel from './ComponentNestingPanel';
import { calcBanquetteCostPreview } from '@/utils/banquette-cost';
import { useComponentCncCost } from '@/hooks/useComponentCncCost';
import { useDrawingViewport } from '@/api/drawing-viewports';
import { usePageTileInfo } from '@/api/knowledge';
import { useEstimateBanquetteLabor } from '@/api/labor-rates';
import { evalMathInput, inchesToFeetDisplay } from '@/utils/math-input';
import { useBanquetteSlotStore } from '@/store/banquetteSlotStore';
import type { BanquetteConfig, BanquetteFraming, MaterialLibrary } from '@estimator/shared';
import type {
  UpholsteryDesign,
  UpholsteryFoamLayer,
  UpholsterySpringConfig,
} from '@estimator/shared';
import { ALL_FACES, type FaceSelection } from '@estimator/shared';
import FabricCoverageSection from './FabricCoverageSection';
import { OversizeControl } from './OversizeControl';
import { toTemplatePayload } from '@/utils/line-item-template';

interface BanquetteBaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  areaId: string;
  projectId: string;
  existingLineItemId?: string;
  existingConfig?: string; // JSON string of BanquetteConfig
  /** Current line item dimensions — used to sync if user edited W/H/D inline */
  existingDimensions?: { width: number; height: number; depth: number };
  templateMode?: boolean;
}

/** Inert design: a base with no upholstery costs exactly what it always did. */
const DEFAULT_DESIGN: UpholsteryDesign = {
  type: 'plain',
  channel_width: null,
  channel_count_override: null,
  button_tuftings_per_foot: null,
  button_hardware_id: null,
  wire_hardware_id: null,
};

const DEFAULT_SPRINGS: UpholsterySpringConfig = {
  springs_enabled: false,
  spring_hardware_id: null,
  spring_mode: 'auto',
  spring_spacing: 4,
  spring_count: 0,
  clip_hardware_id: null,
  connecting_wire_hardware_id: null,
  connecting_wire_count: 1,
};

const DEFAULT_FRAMING: BanquetteFraming = {
  side_panel_material_id: null,
  rib_material_id: null,
  default_finish_id: null,
  side_panel_finish: DEFAULT_PANEL_FINISH,
  rib_finish: DEFAULT_PANEL_FINISH,
  rib_mode: 'auto',
  rib_spacing: 24,
  rib_count: 0,
  waste_factor: 0.10,
  curved_section_count: 0,
  curved_section_waste_factor: 0,
  rails: [
    { name: 'Top Front Rail', width: 5, width_link: null, length: null, length_link: null, material_id: null, board_run_flipped: false, finish: DEFAULT_PANEL_FINISH },
    { name: 'Top Back Rail', width: 5, width_link: null, length: null, length_link: null, material_id: null, board_run_flipped: false, finish: DEFAULT_PANEL_FINISH },
    { name: 'Back Rail', width: 4, width_link: null, length: null, length_link: null, material_id: null, board_run_flipped: false, finish: DEFAULT_PANEL_FINISH },
    { name: 'Bottom Front Nailer', width: 4, width_link: null, length: null, length_link: null, material_id: null, board_run_flipped: false, finish: DEFAULT_PANEL_FINISH },
    { name: 'Front Panel', width: 10, width_link: null, length: null, length_link: null, material_id: null, board_run_flipped: false, finish: DEFAULT_PANEL_FINISH },
  ],
  additional_panels: [],
};

export default function BanquetteBaseModal({
  isOpen, onClose, areaId, projectId,
  existingLineItemId, existingConfig, existingDimensions,
  templateMode = false,
}: BanquetteBaseModalProps) {
  // ── State ──
  const [code, setCode] = useState('BQ');
  const [name, setName] = useState('');
  const [widthInches, setWidthInches] = useState(120); // inches (10 ft default)
  const [widthInputStr, setWidthInputStr] = useState('120');
  const [profileDepth, setProfileDepth] = useState(18);
  const [profileHeight, setProfileHeight] = useState(20);
  const [baseDepth, setBaseDepth] = useState<number | null>(null);
  const [baseHeight, setBaseHeight] = useState<number | null>(null);
  const [baseWidth, setBaseWidth] = useState<number | null>(null);
  const [manualSections, setManualSections] = useState(false);
  const [manualSectionCount, setManualSectionCount] = useState(1);
  const [framing, setFraming] = useState<BanquetteFraming>(DEFAULT_FRAMING);
  const [viewportId, setViewportId] = useState<string | null>(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [fabricDesignPct, setFabricDesignPct] = useState<number | null>(null);
  const [fabricPatternAllowancePct, setFabricPatternAllowancePct] = useState(0);
  const [fabricFaces, setFabricFaces] = useState<FaceSelection>({ ...ALL_FACES });
  // Library pricing only. Absent on a saved config means on.
  const [oversizeEnabled, setOversizeEnabled] = useState(true);
  // ── Upholstery ──
  // Held as state rather than written inert at save time, so a base saved with
  // a design keeps it on the next save. The UI that edits these lands with the
  // Design/Foam/Springs sections.
  const [design, setDesign] = useState<UpholsteryDesign>({ ...DEFAULT_DESIGN });
  const [foamLayers, setFoamLayers] = useState<UpholsteryFoamLayer[]>([]);
  const [foamWasteFactor, setFoamWasteFactor] = useState(0.10);
  const [foamCurvedWasteFactor, setFoamCurvedWasteFactor] = useState(0);
  const [dacronMaterialId, setDacronMaterialId] = useState<string | null>(null);
  const [dacronWasteFactor, setDacronWasteFactor] = useState(0.10);
  const [dacronCurvedWasteFactor, setDacronCurvedWasteFactor] = useState(0);
  const [springs, setSprings] = useState<UpholsterySpringConfig>({ ...DEFAULT_SPRINGS });

  // Seeding rule: the first time any panel on this component is linked, pin the
  // base to the current live dimensions. The `prev == null` guard is what makes
  // this a one-time seed rather than an auto-follow -- an auto-follow would hold
  // every delta at zero forever and the feature would silently do nothing.
  useEffect(() => {
    const linked =
      (framing.rails ?? []).some((r) => r.width_link) ||
      (framing.additional_panels ?? []).some((p) => p.width_link || p.height_link) ||
      // A foam layer may track any axis, so any foam link seeds all three bases.
      foamLayers.some((f) => f.width_link || f.length_link);
    if (!linked) return;
    setBaseDepth((prev) => (prev == null ? profileDepth : prev));
    setBaseHeight((prev) => (prev == null ? profileHeight : prev));
    setBaseWidth((prev) => (prev == null ? widthInches : prev));
  }, [framing, foamLayers, profileDepth, profileHeight, widthInches]);

  // Resolved foam sizes for the row notes. The numeric inputs keep showing the
  // BASE, so the number you typed is always the number you can edit.
  const resolvedFoam = useMemo(
    () => resolveFoamLayers(foamLayers, componentDims({
      lf_quantity: widthInches / 12,
      profile_depth: profileDepth,
      profile_height: profileHeight,
      base_width: baseWidth,
      base_depth: baseDepth,
      base_height: baseHeight,
    })),
    [foamLayers, widthInches, profileDepth, profileHeight, baseWidth, baseDepth, baseHeight],
  );

  const slots = useBanquetteSlotStore((s) => s.slots);
  const setSlots = useBanquetteSlotStore((s) => s.setSlots);
  const updateSlot = useBanquetteSlotStore((s) => s.updateSlot);
  const slotManagerModal = useDisclosure();

  // ── API ──
  const createLineItem = useCreateLineItem();
  const updateLineItem = useUpdateLineItem();
  const { data: materials = [] } = useMaterialsLibrary();
  const { data: finishes = [] } = useFinishes();
  const { data: templates = [] } = useBanquetteBaseTemplates();
  const projectCtx = undefined;

  const isEditMode = !!existingLineItemId;

  // ── Derived ──
  const totalInches = widthInches; // widthInches is already in inches
  const lfQuantity = widthInches / 12; // convert to LF for config/cost calc
  const sectionCount = manualSections
    ? manualSectionCount
    : Math.max(1, Math.ceil(totalInches / 96));
  const sectionLength = sectionCount > 0 ? totalInches / sectionCount : 0;

  // Fetch the viewport's page PPI for cost preview calculations
  const { data: viewportData } = useDrawingViewport(viewportId ?? '');
  const { data: tileInfo } = usePageTileInfo(viewportData?.page_id ?? null);
  const pageScalePpi = tileInfo?.scalePixelsPerUnit ?? undefined;

  // The component library (Cria) renders these modals with no project at all.
  // In that context there is no project Nesting Cost Mode to honour, so sheet
  // goods are priced proportionally — matching the server's ex-fabric path.
  const isLibraryContext = templateMode || !projectId;

  // ── Cost Preview ──
  const { data: hardwareList = [] } = useHardwareLibrary();
  const { data: upholsterySettings } = useUpholsteryDesignSettings();
  // Cached under one key with staleTime: Infinity — UpholsteryDesignSelector
  // below already calls this independently, so this second call costs no
  // extra request. Kept separate rather than prop-drilled through the
  // selector so the cost preview and the selector stay decoupled.
  const { data: designCatalog } = useUpholsteryDesigns();
  const { state: oversizeRules } = useOversizeRulesState();

  const hardwareMap = useMemo(() => {
    const map = new Map<string, { cost_per_unit: number; name: string; unit?: string }>();
    for (const h of hardwareList as any[]) {
      map.set(h.id, { cost_per_unit: h.cost_per_unit ?? 0, name: h.name, unit: h.unit });
    }
    return map;
  }, [hardwareList]);

  const hardwareOptions = useMemo(
    () => (hardwareList as any[]).map((h) => ({ id: h.id, name: h.name })),
    [hardwareList],
  );

  // The config the cost preview prices. Oversize resolves against this same
  // object, so its factor follows the dimensions as they are typed.
  const previewConfig = useMemo((): Partial<BanquetteConfig> => ({
    product_type: 'banquette_base',
    lf_quantity: lfQuantity,
    section_count: sectionCount,
    profile_depth: profileDepth,
    profile_height: profileHeight,
    base_depth: baseDepth,
    base_width: baseWidth,
    base_height: baseHeight,
    design,
    foam_layers: foamLayers,
    foam_waste_factor: foamWasteFactor,
    foam_curved_waste_factor: foamCurvedWasteFactor,
    dacron_material_id: dacronMaterialId,
    dacron_waste_factor: dacronWasteFactor,
    dacron_curved_waste_factor: dacronCurvedWasteFactor,
    springs,
    fabric_design_pct: fabricDesignPct,
    fabric_pattern_allowance_pct: fabricPatternAllowancePct,
    fabric_faces: fabricFaces,
    framing,
    slots,
  }), [widthInches, sectionCount, profileDepth, profileHeight, baseDepth, baseHeight, baseWidth, fabricDesignPct, fabricPatternAllowancePct, fabricFaces, framing, slots, design, foamLayers, foamWasteFactor, foamCurvedWasteFactor, dacronMaterialId, dacronWasteFactor, dacronCurvedWasteFactor, springs]);

  const costPreview = useMemo(() => calcBanquetteCostPreview(
    previewConfig,
    materials as MaterialLibrary[],
    pageScalePpi,
    projectCtx,
    // The component library has no project, so there is no Nesting Cost Mode
    // to honour. Force proportional pricing to match ComponentExFabricService,
    // which already forces used_area for the Cria Layout integration.
    // Keyed off the missing projectId, not templateMode — the library passes
    // templateMode={!editingTemplate}, so it is false while EDITING a template.
    {
      ...(isLibraryContext ? { forceCostMode: 'used_area' as const } : {}),
      finishes,
      designCatalog,
    },
    upholsterySettings?.design_allowance_by_type,
    hardwareMap,
    upholsterySettings?.default_button_hardware_id ?? null,
    upholsterySettings?.default_wire_hardware_id ?? null,
  ), [previewConfig, pageScalePpi, materials, projectCtx, isLibraryContext, finishes, designCatalog, upholsterySettings, hardwareMap]);

  // The base's Total includes fabric, so the factor is never applied to it;
  // the editor only names the factor these dimensions reach.
  const oversize = useMemo(() => resolveOversize(
    oversizeRules, 'banquette_base', { ...previewConfig, oversize_enabled: oversizeEnabled },
  ), [oversizeRules, previewConfig, oversizeEnabled]);

  // ── CNC cost, amortized over the company reference batch ──
  const cnc = useComponentCncCost(
    costPreview?.nesting_parts ?? [],
    materials as MaterialLibrary[],
  );

  // ── Labor Estimate (debounced API call) ──
  const laborEstimate = useEstimateBanquetteLabor();
  const laborTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!isOpen) return;
    if (laborTimerRef.current) clearTimeout(laborTimerRef.current);
    laborTimerRef.current = setTimeout(() => {
      const config = {
        product_type: 'banquette_base',
        lf_quantity: lfQuantity,
        section_count: sectionCount,
        profile_depth: profileDepth,
        profile_height: profileHeight,
        base_depth: baseDepth,
        base_width: baseWidth,
        base_height: baseHeight,
        framing,
        slots,
        // Without this the server sees no selection, and resolveFaces treats an
        // absent value as "all six covered" — scoring fabric cutting, sewing and
        // upholstery mounting on faces the user has deselected.
        fabric_faces: fabricFaces,
        // Upholstery drives tufting, spring, and foam minutes server-side.
        design,
        springs,
        foam_layers: foamLayers,
      };
      laborEstimate.mutate(config);
    }, 500);
    return () => { if (laborTimerRef.current) clearTimeout(laborTimerRef.current); };
  }, [isOpen, widthInches, sectionCount, profileDepth, profileHeight, baseDepth, baseHeight, baseWidth, framing, slots, fabricFaces, design, springs, foamLayers]);

  // ── Load existing config ──
  useEffect(() => {
    if (!isOpen) return;
    if (existingConfig) {
      try {
        const bc = JSON.parse(existingConfig);
        setCode(bc.code ?? 'BQ');
        setName(bc.name ?? '');
        setManualSections(bc.manual_sections ?? false);
        setManualSectionCount(bc.section_count ?? 1);
        setFraming(bc.framing ?? DEFAULT_FRAMING);
        setViewportId(bc.viewport_id ?? null);
        setFabricDesignPct(bc.fabric_design_pct ?? null);
        setFabricPatternAllowancePct(bc.fabric_pattern_allowance_pct ?? 0);
        setFabricFaces({ ...ALL_FACES, ...(bc.fabric_faces ?? {}) });
        setOversizeEnabled(bc.oversize_enabled !== false);
        setDesign({ ...DEFAULT_DESIGN, ...(bc.design ?? {}) });
        setFoamLayers(bc.foam_layers ?? []);
        setFoamWasteFactor(bc.foam_waste_factor ?? 0.10);
        setFoamCurvedWasteFactor(bc.foam_curved_waste_factor ?? 0);
        setDacronMaterialId(bc.dacron_material_id ?? null);
        setDacronWasteFactor(bc.dacron_waste_factor ?? 0.10);
        setDacronCurvedWasteFactor(bc.dacron_curved_waste_factor ?? 0);
        setSprings({ ...DEFAULT_SPRINGS, ...(bc.springs ?? {}) });
        setSlots(bc.slots ?? []);

        // Prioritize line item dimensions over config values (user may have edited inline)
        // Line item width is stored in inches; config lf_quantity is in feet
        if (existingDimensions && existingDimensions.width > 0) {
          const w = existingDimensions.width;
          setWidthInches(w);
          setWidthInputStr(w.toString());
          setProfileHeight(existingDimensions.height > 0 ? existingDimensions.height : (bc.profile_height ?? 20));
          setProfileDepth(existingDimensions.depth > 0 ? existingDimensions.depth : (bc.profile_depth ?? 18));
        } else {
          const w = Math.round((bc.lf_quantity ?? 10) * 12); // convert LF → inches
          setWidthInches(w);
          setWidthInputStr(w.toString());
          setProfileDepth(bc.profile_depth ?? 18);
          setProfileHeight(bc.profile_height ?? 20);
        }
        // Base dims come from the config only, never from existingDimensions.
        // Overwriting them with the line item's live size would zero every
        // delta and silently disable dynamic panels.
        setBaseDepth(bc.base_depth ?? null);
        setBaseWidth(bc.base_width ?? null);
        setBaseHeight(bc.base_height ?? null);
      } catch {
        // Invalid config, use defaults
      }
    }
  }, [isOpen, existingConfig, existingDimensions, setSlots]);

  // ── Template loader ──
  const handleTemplateSelect = useCallback((templateId: string) => {
    setSelectedTemplateId(templateId);
    const tmpl = templates.find((t) => t.id === templateId);
    if (!tmpl) return;
    try {
      const tc = JSON.parse(tmpl.banquette_config || '{}');
      setCode(tc.code ?? tmpl.code);
      setName(tc.name ?? tmpl.name);
      if (tc.profile_depth) setProfileDepth(tc.profile_depth);
      if (tc.profile_height) setProfileHeight(tc.profile_height);
      if (tc.base_depth != null) setBaseDepth(tc.base_depth);
      if (tc.base_width != null) setBaseWidth(tc.base_width);
      if (tc.base_height != null) setBaseHeight(tc.base_height);
      if (tc.framing) setFraming(tc.framing);
      // Don't load slots or viewport from template (project-specific)
    } catch { /* ignore */ }
  }, [templates]);

  // ── Save ──
  const handleSave = async () => {
    if (!name.trim()) { toast.error('Name is required'); return; }
    if (widthInches <= 0) { toast.error('Width must be greater than 0'); return; }

    const banquetteConfig: BanquetteConfig = {
      product_type: 'banquette_base',
      code,
      name: name.trim(),
      lf_quantity: lfQuantity,
      manual_sections: manualSections,
      section_count: sectionCount,
      profile_depth: profileDepth,
      profile_height: profileHeight,
      base_depth: baseDepth,
      base_width: baseWidth,
      base_height: baseHeight,
      design,
      foam_layers: foamLayers,
      foam_waste_factor: foamWasteFactor,
      foam_curved_waste_factor: foamCurvedWasteFactor,
      dacron_material_id: dacronMaterialId,
      dacron_waste_factor: dacronWasteFactor,
      dacron_curved_waste_factor: dacronCurvedWasteFactor,
      springs,
      fabric_design_pct: fabricDesignPct,
      fabric_pattern_allowance_pct: fabricPatternAllowancePct,
      fabric_faces: fabricFaces,
      oversize_enabled: oversizeEnabled,
      viewport_id: viewportId,
      // This literal 0 is what keeps a known client/server pricing divergence
      // dormant (see the KNOWN GAP note in
      // packages/shared/src/utils/template-cost-fixtures.ts and
      // packages/client/src/utils/banquette-cost.ts:381): a non-zero page
      // scale would let the non-fabric slot loop run, and that loop prices a
      // slot differently than the ex-fabric endpoint does. Giving this a real
      // page scale needs the banquette-cost.ts:381 fix FIRST, or the synced
      // price and the displayed price will split.
      image_scale_ppi: 0,
      slots,
      framing,
    };

    const configJson = JSON.stringify(banquetteConfig);

    try {
      if (isEditMode) {
        await updateLineItem.mutateAsync({
          id: existingLineItemId!,
          banquette_config: configJson,
          cabinet_code: code,
          description: name.trim(),
          width: widthInches,
          height: profileHeight,
          depth: profileDepth,
        } as any);
        toast.success('Banquette base updated');
      } else {
        const createPayload: any = {
          area_id: areaId,
          cabinet_code: code,
          description: name.trim(),
          quantity: 1,
          width: widthInches, height: profileHeight, depth: profileDepth,
          num_doors: 0, num_drawers: 0, num_shelves: 0,
          banquette_config: configJson,
          template_id: null, finish_id: null,
          construction_method_id: null,
          construction_overrides: '{}',
          position_x: 0, position_y: 0, position_z: 0,
          is_in_3d_view: false,
          material_overrides: '{}',
          notes: null, sort_order: 0,
        };
        await createLineItem.mutateAsync(templateMode ? toTemplatePayload(createPayload) : createPayload);
        toast.success(templateMode ? 'Banquette template created' : 'Banquette base added');
      }
      handleReset();
      onClose();
    } catch {
      toast.error(isEditMode ? 'Failed to update banquette base' : 'Failed to create banquette base');
    }
  };

  const handleReset = () => {
    setCode('BQ');
    setName('');
    setWidthInches(120);
    setWidthInputStr('120');
    setProfileDepth(18);
    setProfileHeight(20);
    setBaseDepth(null);
    setBaseWidth(null);
    setBaseHeight(null);
    setManualSections(false);
    setManualSectionCount(1);
    setFraming(DEFAULT_FRAMING);
    setViewportId(null);
    setSelectedTemplateId('');
    setDesign({ ...DEFAULT_DESIGN });
    setFoamLayers([]);
    setFoamWasteFactor(0.10);
    setFoamCurvedWasteFactor(0);
    setDacronMaterialId(null);
    setDacronWasteFactor(0.10);
    setDacronCurvedWasteFactor(0);
    setSprings({ ...DEFAULT_SPRINGS });
    setOversizeEnabled(true);
    useBanquetteSlotStore.getState().clearAll();
  };

  const isPending = createLineItem.isPending || updateLineItem.isPending;

  return (
    <>
    <ResizableModal
      isOpen={isOpen}
      onClose={onClose}
      storageKey="banquette-base"
      classNames={{
        base: 'bg-[var(--bg-surface)]',
        header: 'border-b border-[var(--border)]',
        footer: 'border-t border-[var(--border)]',
      }}
    >
        <ModalHeader className="text-sm font-semibold flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
          <Sofa size={16} style={{ color: '#e67e22' }} />
          {isEditMode ? 'Edit Banquette Base' : 'New Banquette Base'}
        </ModalHeader>
        <ModalBody className="py-0 px-0 gap-0">
          {/* ── Sticky Cost Bar ── */}
          <div className="sticky top-0 z-10 px-6 py-2" style={{ backgroundColor: 'var(--bg-surface)' }}>
            <BanquetteCostPreview
              cost={costPreview}
              lfQuantity={lfQuantity}
              laborCost={laborEstimate.data?.total_labor_cost}
              laborDetails={laborEstimate.data?.labor_details}
              cncCost={cnc.total_cost_per_unit}
            />
            {/* Library only: a project line item is never charged oversize. */}
            {isLibraryContext && (
              <div className="mt-1.5">
                <OversizeControl
                  enabled={oversizeEnabled}
                  onChange={setOversizeEnabled}
                  result={oversize}
                />
              </div>
            )}
          </div>

          {/* ── Single Column Config ── */}
          <div className="px-6 py-4 space-y-4">

            {/* Template + Header row */}
            <div className="grid grid-cols-12 gap-3">
              {!isEditMode && templates.length > 0 && (
                <div className="col-span-4">
                  <Select
                    label="Template"
                    selectedKeys={selectedTemplateId ? [selectedTemplateId] : []}
                    onSelectionChange={(keys) => {
                      const id = Array.from(keys)[0] as string;
                      if (id) handleTemplateSelect(id);
                    }}
                    size="sm"
                    placeholder="Load template..."
                    classNames={{ trigger: 'bg-[var(--bg-panel)]' }}
                  >
                    {templates.map((t) => (
                      <SelectItem key={t.id}>{t.code} — {t.name}</SelectItem>
                    ))}
                  </Select>
                </div>
              )}
              <div className={!isEditMode && templates.length > 0 ? 'col-span-2' : 'col-span-3'}>
                <Input label="Code" value={code} onValueChange={setCode} size="sm"
                  classNames={{ inputWrapper: 'bg-[var(--bg-panel)]' }} />
              </div>
              <div className={!isEditMode && templates.length > 0 ? 'col-span-6' : 'col-span-9'}>
                <Input label="Name *" value={name} onValueChange={setName} size="sm" isRequired
                  classNames={{ inputWrapper: 'bg-[var(--bg-panel)]' }} />
              </div>
            </div>

            {/* Run Configuration + Profile Dimensions — all in one row */}
            <div>
              <h3 className="text-xs font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>Dimensions</h3>
              <div className="grid grid-cols-7 gap-3">
                <div>
                  <Input label="Width" value={widthInputStr}
                    onValueChange={setWidthInputStr}
                    onBlur={() => {
                      const result = evalMathInput(widthInputStr);
                      if (result !== null && result >= 0) {
                        const rounded = Math.round(result);
                        setWidthInches(rounded);
                        setWidthInputStr(rounded.toString());
                      } else {
                        setWidthInputStr(widthInches.toString());
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        (e.target as HTMLInputElement).blur();
                      }
                    }}
                    size="sm"
                    classNames={{ inputWrapper: 'bg-[var(--bg-panel)]' }}
                    endContent={<span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>in</span>}
                    description={widthInches > 0 ? inchesToFeetDisplay(widthInches) : undefined}
                  />
                </div>
                <Input label="Depth" type="number" value={profileDepth.toString()}
                  onValueChange={(v) => setProfileDepth(Number(v) || 0)} size="sm"
                  classNames={{ inputWrapper: 'bg-[var(--bg-panel)]' }}
                  endContent={<span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>in</span>} />
                <Input label="Height" type="number" value={profileHeight.toString()}
                  onValueChange={(v) => setProfileHeight(Number(v) || 0)} size="sm"
                  classNames={{ inputWrapper: 'bg-[var(--bg-panel)]' }}
                  endContent={<span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>in</span>} />
                <Input label="Base Height" type="number"
                  value={(baseHeight ?? profileHeight).toString()}
                  onValueChange={(v) => setBaseHeight(Number(v) || 0)} size="sm"
                  classNames={{ inputWrapper: 'bg-[var(--bg-panel)]' }}
                  description="Dynamic panel reference"
                  endContent={<span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>in</span>} />
                <Input label="Base Depth" type="number"
                  value={(baseDepth ?? profileDepth).toString()}
                  onValueChange={(v) => setBaseDepth(Number(v) || 0)} size="sm"
                  classNames={{ inputWrapper: 'bg-[var(--bg-panel)]' }}
                  description="Dynamic panel reference"
                  endContent={<span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>in</span>} />
                <Input label="Base Width" type="number"
                  value={(baseWidth ?? widthInches).toString()}
                  onValueChange={(v) => setBaseWidth(Number(v) || 0)} size="sm"
                  classNames={{ inputWrapper: 'bg-[var(--bg-panel)]' }}
                  description="Dynamic foam reference"
                  endContent={<span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>in</span>} />
                <div className="col-span-2 flex flex-col justify-center">
                  <div className="flex items-center gap-2 mb-1">
                    <Switch size="sm" isSelected={manualSections} onValueChange={setManualSections}>
                      <span className="text-xs">Manual sections</span>
                    </Switch>
                  </div>
                  {manualSections ? (
                    <Input type="number" value={manualSectionCount.toString()}
                      onValueChange={(v) => setManualSectionCount(Math.max(1, Number(v) || 1))}
                      size="sm" label="Sections"
                      classNames={{ inputWrapper: 'bg-[var(--bg-panel)]' }} />
                  ) : (
                    <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                      {sectionCount} section{sectionCount !== 1 ? 's' : ''} x {sectionLength.toFixed(1)}" each
                      {sectionLength > 96 && <span style={{ color: '#e67e22' }}> (exceeds 96")</span>}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <Divider className="bg-[var(--border)]" />

            {/* Framing Config — expanded by default, full width */}
            <BanquetteFramingConfig
              framing={framing}
              materials={materials as MaterialLibrary[]}
              onChange={setFraming}
              widthInches={widthInches}
              sectionCount={sectionCount}
              dims={{
                width: widthInches,
                depth: profileDepth,
                height: profileHeight,
                run_axis: 'width' as const,
                base_width: baseWidth,
                base_depth: baseDepth,
                base_height: baseHeight,
              }}
            />

            <Divider className="bg-[var(--border)]" />

            {/* Viewport + Slot Manager — side by side */}
            <div className="grid grid-cols-2 gap-4">
              <BanquetteViewportSelector
                projectId={projectId}
                viewportId={viewportId}
                onViewportChange={(id) => setViewportId(id)}
              />
              <div className="flex flex-col justify-end">
                <Button
                  fullWidth
                  variant="bordered"
                  onPress={slotManagerModal.onOpen}
                  isDisabled={!viewportId}
                  style={{ borderColor: '#e67e22', color: '#e67e22' }}
                >
                  Open Slot Manager ({slots.length} slot{slots.length !== 1 ? 's' : ''})
                </Button>
              </div>
            </div>

            {/* Finish assignments for slots with materials requiring finish */}
            {slots.length > 0 && (
              <>
                <Divider className="bg-[var(--border)]" />
                <BanquetteFinishSummary
                  slots={slots}
                  materials={materials as MaterialLibrary[]}
                  onUpdateSlot={updateSlot}
                />
              </>
            )}

            <Divider className="bg-[var(--border)]" />

            {/* ── Upholstery Design ──
                Above the fabric allowances because the design type is what
                resolves the allowance percentage. */}
            <div>
              <h3 className="text-xs font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
                Upholstery Design
              </h3>
              <UpholsteryDesignSelector
                design={design}
                onChange={setDesign}
                runDimensionInches={sectionLength}
                runDimensionLabel="section width"
                crossDimensionInches={profileHeight}
                crossDimensionLabel="height"
                hardwareOptions={hardwareOptions}
              />
            </div>

            <Divider className="bg-[var(--border)]" />

            {/* ── Foam & Dacron ──
                Optional: an upholstered base may be padded or fabric straight
                over the panel. Empty layer list is the default. */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>
                  Foam &amp; Dacron
                </h3>
                <Button
                  size="sm"
                  variant="flat"
                  onPress={() =>
                    setFoamLayers([...foamLayers, { name: `Layer ${foamLayers.length + 1}`, width: 0, width_link: null, length: null, length_link: null, material_id: null }])
                  }
                >
                  + Add Layer
                </Button>
              </div>

              {foamLayers.length === 0 && (
                <div className="text-[10px] mb-2" style={{ color: 'var(--text-muted)' }}>
                  No foam — fabric goes straight over the panel.
                </div>
              )}

              {foamLayers.map((layer, i) => (
                <div key={i} className="flex gap-2 items-end mb-2">
                  <Input
                    label="Name"
                    size="sm"
                    className="flex-1"
                    value={layer.name}
                    onValueChange={(v) => {
                      const next = [...foamLayers];
                      next[i] = { ...next[i], name: v };
                      setFoamLayers(next);
                    }}
                  />
                  <Input
                    label="Width"
                    type="number"
                    size="sm"
                    className="w-28"
                    value={String(layer.width)}
                    onValueChange={(v) => {
                      const next = [...foamLayers];
                      next[i] = { ...next[i], width: Number(v) || 0 };
                      setFoamLayers(next);
                    }}
                    endContent={<span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>in</span>}
                  />
                  <PanelDimControls
                    label="Width"
                    link={layer.width_link ?? null}
                    onChange={(link) => {
                      const next = [...foamLayers];
                      next[i] = { ...next[i], width_link: link };
                      setFoamLayers(next);
                    }}
                  />
                  <Input
                    label="Length"
                    type="number"
                    size="sm"
                    className="w-28"
                    placeholder="auto"
                    value={layer.length == null ? '' : String(layer.length)}
                    onValueChange={(v) => {
                      const next = [...foamLayers];
                      next[i] = { ...next[i], length: v === '' ? null : (Number(v) || 0) };
                      setFoamLayers(next);
                    }}
                    endContent={<span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>in</span>}
                  />
                  <PanelDimControls
                    label="Length"
                    link={layer.length_link ?? null}
                    onChange={(link) => {
                      const next = [...foamLayers];
                      // Leaving Auto has to concretize a base, or the row would
                      // show an empty input with no number to edit.
                      const base = next[i].length ?? Math.round(widthInches);
                      next[i] = { ...next[i], length: base, length_link: link };
                      setFoamLayers(next);
                    }}
                  />
                  <Select
                    label="Material"
                    size="sm"
                    className="w-52"
                    selectedKeys={layer.material_id ? [layer.material_id] : []}
                    onSelectionChange={(keys) => {
                      const next = [...foamLayers];
                      next[i] = { ...next[i], material_id: (Array.from(keys)[0] as string) ?? null };
                      setFoamLayers(next);
                    }}
                  >
                    {(materials as MaterialLibrary[]).map((m) => (
                      <SelectItem key={m.id}>{m.name}</SelectItem>
                    ))}
                  </Select>
                  <Button
                    size="sm"
                    variant="light"
                    color="danger"
                    onPress={() => setFoamLayers(foamLayers.filter((_, j) => j !== i))}
                  >
                    Remove
                  </Button>
                  {resolvedFoam[i] && (
                    <span
                      className="text-[10px] flex-shrink-0 w-32 text-right pb-2"
                      style={{ color: resolvedFoam[i].collapsed ? '#e67e22' : 'var(--text-muted)' }}
                    >
                      {resolvedFoam[i].collapsed
                        ? 'collapsed'
                        : `${resolvedFoam[i].width} x ${Math.round(resolvedFoam[i].length)} in · ${((resolvedFoam[i].width * resolvedFoam[i].length) / 144).toFixed(1)} sqft`}
                    </span>
                  )}
                </div>
              ))}

              <div className="flex gap-3 mt-2">
                <Input
                  label="Foam Waste (%)"
                  type="number"
                  size="sm"
                  className="w-36"
                  value={String(foamWasteFactor * 100)}
                  onValueChange={(v) => setFoamWasteFactor((Number(v) || 0) / 100)}
                />
                <Input
                  label="Curved Foam Waste (%)"
                  type="number"
                  size="sm"
                  className="w-44"
                  value={String(foamCurvedWasteFactor * 100)}
                  onValueChange={(v) => setFoamCurvedWasteFactor((Number(v) || 0) / 100)}
                  description="Extra, on curved sections only"
                />
                <Select
                  label="Dacron Material"
                  size="sm"
                  className="w-52"
                  selectedKeys={dacronMaterialId ? [dacronMaterialId] : []}
                  onSelectionChange={(keys) =>
                    setDacronMaterialId((Array.from(keys)[0] as string) ?? null)
                  }
                >
                  {(materials as MaterialLibrary[]).map((m) => (
                    <SelectItem key={m.id}>{m.name}</SelectItem>
                  ))}
                </Select>
                <Input
                  label="Dacron Waste (%)"
                  type="number"
                  size="sm"
                  className="w-36"
                  value={String(dacronWasteFactor * 100)}
                  onValueChange={(v) => setDacronWasteFactor((Number(v) || 0) / 100)}
                />
              </div>
            </div>

            <Divider className="bg-[var(--border)]" />

            {/* ── Springs ── */}
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h3 className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>
                  Springs
                </h3>
                <Switch
                  size="sm"
                  isSelected={springs.springs_enabled}
                  onValueChange={(v) => setSprings({ ...springs, springs_enabled: v })}
                >
                  <span className="text-xs">{springs.springs_enabled ? 'Enabled' : 'Disabled'}</span>
                </Switch>
              </div>

              {springs.springs_enabled && (
                <div className="space-y-2">
                  <div className="flex gap-3 items-end">
                    <Select
                      label="Spring Hardware"
                      size="sm"
                      className="w-52"
                      selectedKeys={springs.spring_hardware_id ? [springs.spring_hardware_id] : []}
                      onSelectionChange={(keys) =>
                        setSprings({ ...springs, spring_hardware_id: (Array.from(keys)[0] as string) ?? null })
                      }
                    >
                      {hardwareOptions.map((h) => (
                        <SelectItem key={h.id}>{h.name}</SelectItem>
                      ))}
                    </Select>
                    <Switch
                      size="sm"
                      isSelected={springs.spring_mode === 'manual'}
                      onValueChange={(v) => setSprings({ ...springs, spring_mode: v ? 'manual' : 'auto' })}
                    >
                      <span className="text-xs">{springs.spring_mode === 'manual' ? 'Manual' : 'Auto'}</span>
                    </Switch>
                    {springs.spring_mode === 'manual' ? (
                      <Input
                        label="Spring Count"
                        type="number"
                        size="sm"
                        className="w-32"
                        value={String(springs.spring_count)}
                        onValueChange={(v) =>
                          setSprings({ ...springs, spring_count: Math.max(0, Math.floor(Number(v) || 0)) })
                        }
                      />
                    ) : (
                      <Input
                        label="Spacing"
                        type="number"
                        size="sm"
                        className="w-32"
                        value={String(springs.spring_spacing)}
                        onValueChange={(v) => setSprings({ ...springs, spring_spacing: Number(v) || 0 })}
                        endContent={<span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>in</span>}
                      />
                    )}
                  </div>
                  <div className="flex gap-3 items-end">
                    <Select
                      label="Clip Hardware"
                      size="sm"
                      className="w-52"
                      selectedKeys={springs.clip_hardware_id ? [springs.clip_hardware_id] : []}
                      onSelectionChange={(keys) =>
                        setSprings({ ...springs, clip_hardware_id: (Array.from(keys)[0] as string) ?? null })
                      }
                    >
                      {hardwareOptions.map((h) => (
                        <SelectItem key={h.id}>{h.name}</SelectItem>
                      ))}
                    </Select>
                    <Select
                      label="Connecting Wire"
                      size="sm"
                      className="w-52"
                      selectedKeys={
                        springs.connecting_wire_hardware_id ? [springs.connecting_wire_hardware_id] : []
                      }
                      onSelectionChange={(keys) =>
                        setSprings({
                          ...springs,
                          connecting_wire_hardware_id: (Array.from(keys)[0] as string) ?? null,
                        })
                      }
                    >
                      {hardwareOptions.map((h) => (
                        <SelectItem key={h.id}>{h.name}</SelectItem>
                      ))}
                    </Select>
                    <Input
                      label="Wire Runs"
                      type="number"
                      size="sm"
                      className="w-32"
                      value={String(springs.connecting_wire_count)}
                      onValueChange={(v) =>
                        setSprings({
                          ...springs,
                          connecting_wire_count: Math.max(0, Math.floor(Number(v) || 0)),
                        })
                      }
                    />
                  </div>
                  {costPreview && costPreview.spring_count > 0 && (
                    <div
                      className="text-[10px] px-2 py-1 rounded"
                      style={{ backgroundColor: 'var(--bg-surface)', color: 'var(--text-secondary)' }}
                    >
                      {costPreview.spring_count} springs, {costPreview.clip_count} clips,{' '}
                      {costPreview.connecting_wire_lf.toFixed(1)} lf connecting wire
                    </div>
                  )}
                </div>
              )}
            </div>

            <Divider className="bg-[var(--border)]" />

            {/* Fabric Allowances */}
            {(() => {
              const isFabricMat = (m: any) => !!(m?.fabric_subtype) || ((m?.material_type ?? '') as string).toLowerCase() === 'fabric';
              const hasFabricSlot = slots.some((s) => {
                if (!s.material_id) return false;
                return isFabricMat((materials as MaterialLibrary[]).find((mat) => mat.id === s.material_id));
              });
              if (!hasFabricSlot) return null;
              const firstFabricMat = slots
                .filter((s) => s.material_id)
                .map((s) => (materials as MaterialLibrary[]).find((mat) => mat.id === s.material_id))
                .find((m) => isFabricMat(m));
              const matDefault = (firstFabricMat as any)?.fabric_design_pct ?? 0;
              return (
                <div className="flex gap-3">
                  <Input
                    label="Design Allowance (%)"
                    type="number"
                    size="sm"
                    className="w-44"
                    value={fabricDesignPct != null ? String(fabricDesignPct * 100) : ''}
                    placeholder={`Mat default: ${(matDefault * 100).toFixed(0)}%`}
                    onValueChange={(v) => {
                      if (v === '') { setFabricDesignPct(null); return; }
                      setFabricDesignPct((Number(v) || 0) / 100);
                    }}
                    description="Overrides material default"
                  />
                  <Input
                    label="Pattern Allowance (%)"
                    type="number"
                    size="sm"
                    className="w-44"
                    value={String(fabricPatternAllowancePct * 100)}
                    onValueChange={(v) => setFabricPatternAllowancePct((Number(v) || 0) / 100)}
                    description="Pattern repeat allowance"
                  />
                </div>
              );
            })()}

            <FabricCoverageSection
              productType="banquette_base"
              dims={{ width_in: sectionLength, height_in: profileHeight, depth_in: profileDepth }}
              value={fabricFaces}
              onChange={setFabricFaces}
            />

            {/* Finish Schedule (read-only summary grouped by material type) */}
            <Accordion variant="bordered">
              <AccordionItem
                key="finish-schedule"
                title="Finish Schedule"
                classNames={{ base: 'border-[var(--border)]', title: 'text-xs', trigger: 'py-2' }}
              >
                <FinishSchedule
                  slotDetails={costPreview?.slot_details ?? []}
                  materials={materials as MaterialLibrary[]}
                  fabricPackings={costPreview?.fabric_packings ?? []}
                />
              </AccordionItem>
              <AccordionItem
                key="nesting-preview"
                title="Nesting Preview"
                classNames={{ base: 'border-[var(--border)]', title: 'text-xs', trigger: 'py-2' }}
              >
                <ComponentNestingPanel
                  parts={costPreview?.nesting_parts ?? []}
                  materials={materials as MaterialLibrary[]}
                  cnc={cnc}
                />
              </AccordionItem>
            </Accordion>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="light" onPress={onClose}>Cancel</Button>
          <Button
            onPress={handleSave}
            isLoading={isPending}
            style={{ backgroundColor: '#e67e22', color: 'white' }}
          >
            {isEditMode ? 'Update' : 'Create'}
          </Button>
        </ModalFooter>
    </ResizableModal>

    {/* Slot Manager fullscreen modal */}
    {viewportId && (
      <BanquetteSlotManager
        isOpen={slotManagerModal.isOpen}
        onClose={slotManagerModal.onClose}
        viewportId={viewportId}
        projectId={projectId}
        productType="banquette_base"
        profileDepth={profileDepth}
        profileHeight={profileHeight}
      />
    )}
    </>
  );
}
